import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import Head from "next/head";
import { makeStyles } from "@material-ui/core/styles";
import HeroSection from "./HeroSection";
import Navbar from "./Navbar";
import Overview from "./Overview";
import BecomeMember from "./BecomeMember";
import Community from "./Community";
import Featured from "./Featured";
import SuccessStory from "./SuccessStory";
import Testimony from "./Testimony";
import FAQ from "./FAQ";
import Footer from "./Footer";
import WhyMyfitsociety from "./WhyMyfitsociety";
import WorkoutPlan from "./WorkoutPlan";
import YourProgram from "./YourProgram";
import Comparison from "./Comparison";
import Plan, { getHighestFlashdealCardPercentage } from "./Plan";
import seo, { landingSeoFor } from "../../static/text/seo";
import { faqQuestionData } from "../../static/new-landing-page/new-landing-data";
import { getFaqJsonLdAnswerText } from "../../helpers/faqJsonLd";
import {
  followLandingCta,
  getLandingQuestionnaireUrl,
  resolveLandingCtaContext,
} from "../../helpers/landingQuestionnaireUrl";
import { getInternalWorkoutLanding } from "../../helpers/internalRequest";
import { isOutsideIndonesiaCountry } from "../../helpers/languageSwitchVisibility";
import {
  calculateCleengDiscountPercentage,
  formatCleengDiscountPercentage,
} from "../../helpers/cleengPricing";

export { default as Testimony } from "./Testimony";
export { default as TestimonyDesktop } from "./TestimonyDesktop";
export { default as TestimonyMobile } from "./TestimonyMobile";

// Hero adalah kandidat LCP — preload supaya tidak menunggu CSS/JS selesai.
// File sama dengan require() di HeroSection.js → webpack dedupe jadi satu URL.
const heroImageDesktopEN = require("../../static/new-landing-page/hero-section/en/desktop_hero_en.webp");
const heroImageMobileEN = require("../../static/new-landing-page/hero-section/en/mobile_hero_en.webp");
const heroImageDesktopID = require("../../static/new-landing-page/hero-section/id/desktop_hero_id.webp");
const heroImageMobileID = require("../../static/new-landing-page/hero-section/id/mobile_hero_id.webp");

// Rating strip di atas hero adalah kandidat LCP mobile — preload varian
// viewport-nya supaya tidak antre di belakang JS.
const ratingBadgeSm = require("../../static/new-landing-page/wrath_white_appstore_playstore_2026_440w.webp");
const ratingBadgeLg = require("../../static/new-landing-page/wrath_white_appstore_playstore_2026.webp");

// FAQPage JSON-LD dari data FAQ yang sama dengan yang di-render — search &
// AI engines membaca Q&A tanpa perlu menjalankan JS.
const buildFaqJsonLd = (language) => {
  const faqs = faqQuestionData[language] || faqQuestionData.en;
  if (!Array.isArray(faqs) || faqs.length === 0) {
    return null;
  }
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs
      .filter((faq) => faq && faq.question && faq.answer)
      .map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          // JSON-LD copy only: no "click here <link label>" UI wording.
          text: getFaqJsonLdAnswerText(faq),
        },
      })),
  });
};

const CTA_PERCENTAGE_PAGE_SOURCES = ["promo", "plan", "promo/subs3"];

// Trello 399 / 26 AUG 26: /flashdeals now owns its admin-configured package
// section alongside /plan. Other marketing landings keep CTA-only pricing.
export const shouldRenderLandingPackageCards = (pageSource) => (
  pageSource === "plan" || pageSource === "flashdeals"
);

export const hasUsableLandingPackages = (content) => (
  !!content
  && Array.isArray(content.plan)
  && content.plan.some(
    (group) => group && Array.isArray(group.packages) && group.packages.length > 0
  )
);

export const selectLandingPackageContent = (...sources) => (
  sources.find(hasUsableLandingPackages) || null
);

export const hasUsableLandingPercentagePackages = (content) => (
  hasUsableLandingPackages(content)
  || !!(
    content
    && Array.isArray(content.selling_price_plan)
    && content.selling_price_plan.some(
      (group) => group && Array.isArray(group.packages) && group.packages.length > 0
    )
  )
);

export const selectLandingPercentageContent = (...sources) => (
  sources.find(hasUsableLandingPercentagePackages) || null
);

export const normalizeLandingPricingCountry = (country) => {
  const normalized = typeof country === "string" ? country.trim().toUpperCase() : "";
  if (normalized === "WORLDWIDE") {
    return "US";
  }
  return /^[A-Z]{2}$/.test(normalized) ? normalized : "ID";
};

// Pages whose CTA button % must equal the highest "Save X%"/"Diskon X%" badge on
// the displayed package cards (sourced from discount_label/discount_caption).
// Flashsale keeps the fs_* path; flashdeals uses its dedicated four-card builder.
const CARD_LABEL_PERCENTAGE_PAGE_SOURCES = ["promo", "plan", "premium", "deals"];

const getLandingPricingGroupIndex = (content) => {
  if (!content || !Array.isArray(content.plan) || content.plan.length <= 1) {
    return 0;
  }

  const types = content.enabled_package_types || {};
  if (
    (types.subscription && types.single_purchase)
    || (types.subscription && types.single_purchase === false)
  ) {
    return 0;
  }

  return 1;
};

const getPackageDiscountPercentage = (item) => {
  if (!item) {
    return null;
  }

  if (item.cleeng_price && item.cleeng_price.source === "cleeng") {
    const cleengDiscountPercentage = calculateCleengDiscountPercentage(item.cleeng_price);
    return cleengDiscountPercentage;
  }

  const numericCandidates = [
    item.discount_percentage,
    item.promo_category_1_percentage,
    item.highest_percentage,
    item.percentage,
    item.fs_pl_first_pct,
    item.fs_pl_recurring_pct,
    item.fs_fp_first_pct,
    item.fs_fp_recurring_pct,
  ];

  for (let index = 0; index < numericCandidates.length; index += 1) {
    const value = Number(numericCandidates[index]);
    if (Number.isFinite(value) && value > 0) {
      return value;
    }
  }

  const textCandidates = [item.discount_label, item.discount_caption];
  for (let index = 0; index < textCandidates.length; index += 1) {
    const value = textCandidates[index];
    if (value) {
      const matchedValue = String(value).match(/(\d+(?:\.\d+)?)\s*%/);
      if (matchedValue) {
        const parsedValue = Number(matchedValue[1]);
        if (Number.isFinite(parsedValue) && parsedValue > 0) {
          return parsedValue;
        }
      }
    }
  }

  return null;
};

// Card-label percentage extractor: read the integer % the package CARD actually
// renders (components/PlanPrice renderTextTile -> item.discount_label, fallback
// item.discount_caption). This deliberately ignores the numeric flashsale fields
// (fs_pl_first_pct, etc.) so the button mirrors the card badge string, not the
// short-circuited flashsale percentage.
export const getCardLabelPercentage = (item) => {
  if (!item) {
    return null;
  }

  // Cleeng dynamic-pricing cards render the Cleeng-computed badge, NOT the legacy
  // discount_label string. components/PlanPrice builds the badge as
  // formatCleengDiscountPercentage(calculateCleengDiscountPercentage(cleeng_price))
  // (= the get-plan / final-summary "Save X%": floor(toFixed(1) of retail vs the
  // first-cycle price)). The raw discount_label here is the stale legacy
  // direct_discount / promo % (premium ~66, promo ~80), which does NOT match the
  // card. Mirror the card's own value so the CTA button equals the HIGHEST % the
  // final-summary shows (US: premium 94, promo/plan 99).
  if (item.cleeng_price && item.cleeng_price.source === "cleeng") {
    const cleengPercentage = formatCleengDiscountPercentage(
      calculateCleengDiscountPercentage(item.cleeng_price),
    );
    return Number.isFinite(cleengPercentage) && cleengPercentage > 0
      ? cleengPercentage
      : null;
  }

  const textCandidates = [item.discount_label, item.discount_caption];
  for (let index = 0; index < textCandidates.length; index += 1) {
    const value = textCandidates[index];
    if (value) {
      const matchedValue = String(value).match(/(\d+(?:\.\d+)?)\s*%/);
      if (matchedValue) {
        const parsedValue = Number(matchedValue[1]);
        if (Number.isFinite(parsedValue) && parsedValue > 0) {
          return parsedValue;
        }
      }
    }
  }

  return null;
};

// MAX card-label percentage over the SAME group the cards render (mirrors
// layout/landing-v2/Plan.js getPackageList -> planContent.plan[groupIndex].packages).
// Used for page_source promo / plan / premium so the CTA button equals the
// highest "Save X%" / "Diskon X%" badge shown on the displayed package cards.
export const getHighestCardLabelPercentage = (content) => {
  if (!content) {
    return null;
  }

  const planGroups = Array.isArray(content.plan) ? content.plan : [];
  let group = planGroups[getLandingPricingGroupIndex(content)]
    || planGroups.find((g) => g && Array.isArray(g.packages) && g.packages.length)
    || null;

  if (!group || !Array.isArray(group.packages) || !group.packages.length) {
    const sellingGroups = Array.isArray(content.selling_price_plan)
      ? content.selling_price_plan
      : [];
    group = sellingGroups[getLandingPricingGroupIndex(content)]
      || sellingGroups.find((g) => g && Array.isArray(g.packages) && g.packages.length)
      || null;
  }

  if (!group || !Array.isArray(group.packages) || !group.packages.length) {
    return null;
  }

  const highestPercentage = group.packages.reduce((currentHighest, item) => {
    const itemPercentage = getCardLabelPercentage(item);
    if (!Number.isFinite(itemPercentage)) {
      return currentHighest;
    }
    return Math.max(currentHighest, itemPercentage);
  }, 0);

  return highestPercentage > 0 ? highestPercentage : null;
};

const getHighestActivePackagePercentage = (content, pageSource) => {
  if (!content || !Array.isArray(content.plan) || !content.plan.length) {
    return null;
  }

  const isFlashsaleContext = pageSource === "flashsale" || pageSource === "/flashsale";

  const groups = isFlashsaleContext
    ? content.plan.filter((g) => g && Array.isArray(g.packages) && g.packages.length)
    : [
        content.plan[getLandingPricingGroupIndex(content)]
        || content.plan.find((group) => group && Array.isArray(group.packages) && group.packages.length)
        || null,
      ].filter(Boolean);

  const packages = groups.reduce((acc, g) => acc.concat(g.packages || []), []);

  const highestPercentage = packages.reduce((currentHighest, item) => {
    if (isFlashsaleContext && item) {
      const fsCandidates = [
        item.fs_pl_first_pct,
        item.fs_fp_first_pct,
        item.fs_pl_recurring_pct,
        item.fs_fp_recurring_pct,
      ];
      const maxFs = fsCandidates.reduce((best, v) => {
        const n = Number(v);
        return Number.isFinite(n) && n > best ? n : best;
      }, 0);
      if (maxFs > 0) {
        return Math.max(currentHighest, maxFs);
      }
    }

    const itemPercentage = getPackageDiscountPercentage(item);
    if (!Number.isFinite(itemPercentage)) {
      return currentHighest;
    }

    return Math.max(currentHighest, itemPercentage);
  }, 0);

  return highestPercentage > 0 ? highestPercentage : null;
};

const useStyles = makeStyles(() => ({
  // root: {
  //     fontFamily: 'Inter,Inter Fallback'
  // },
}));

const LandingPageV2 = ({
  initialLanguage,
  page_source = null,
  cms_slug = null,
  cfCountry = null,
  additionalProps,
  planContent = null,
  pricingContent = null,
  landingPageContent = { highest_percentage: null },
  isFlashdeals = false,
  // '/' renders this layout with page_source 'premium' for pricing — but it is
  // the homepage, so it must keep the homepage title/description (its own
  // <Head> would otherwise be overridden by the nested one below).
  useHomepageSeo = false,
}) => {
  const classes = useStyles();
  const router = useRouter();
  const [fetchedPlanContent, setFetchedPlanContent] = useState(null);
  const pathName = (typeof window !== "undefined" && window.location && window.location.pathname)
    || (router.asPath ? String(router.asPath).split("?")[0] : "");
  const resolvedCta = resolveLandingCtaContext({
    pageSource: page_source,
    additionalProps,
    query: router.query,
    pathname: pathName,
  });
  const resolvedSource = resolvedCta.pageSource;
  const shouldRenderFlashdeals = resolvedSource === "flashdeals"
    || isFlashdeals
    || (additionalProps && additionalProps.isFlashdeals);
  const needsPackageCards = shouldRenderLandingPackageCards(resolvedSource);
  const resolvedPricingCountry = normalizeLandingPricingCountry(
    (router.query && router.query.country) || cfCountry
  );
  const pricingRequestKey = [
    resolvedSource || "premium",
    initialLanguage || "id",
    resolvedPricingCountry,
  ].join(":");
  const [pricingRetryTick, setPricingRetryTick] = useState(0);
  const pricingAttemptRef = useRef({ key: null, count: 0 });
  const fetchedPricingKeyRef = useRef(null);
  const currentFetchedPlanContent = fetchedPricingKeyRef.current === pricingRequestKey
    ? fetchedPlanContent
    : null;

  if (pricingAttemptRef.current.key !== pricingRequestKey) {
    pricingAttemptRef.current = { key: pricingRequestKey, count: 0 };
  }

  useEffect(() => {
    if (
      hasUsableLandingPackages(planContent)
      || hasUsableLandingPackages(pricingContent)
      || hasUsableLandingPackages(currentFetchedPlanContent)
      || !needsPackageCards
      || pricingAttemptRef.current.count >= 2
    ) {
      return undefined;
    }
    pricingAttemptRef.current.count += 1;
    let cancelled = false;
    getInternalWorkoutLanding({
      language: initialLanguage || "id",
      page_source: resolvedSource || "premium",
      promo_price: resolvedSource === "promo"
        || resolvedSource === "plan"
        || resolvedSource === "flashdeals",
      context: resolvedSource === "flashdeals" ? "flashsale" : undefined,
      country: resolvedPricingCountry,
    }).then((res) => {
      if (cancelled) return;
      if (res && hasUsableLandingPackages(res.data)) {
        fetchedPricingKeyRef.current = pricingRequestKey;
        setFetchedPlanContent(res.data);
        return;
      }
      if (pricingAttemptRef.current.count < 2) {
        setPricingRetryTick((tick) => tick + 1);
      }
    }).catch(() => {
      if (!cancelled && pricingAttemptRef.current.count < 2) {
        setPricingRetryTick((tick) => tick + 1);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    planContent,
    pricingContent,
    currentFetchedPlanContent,
    needsPackageCards,
    resolvedSource,
    initialLanguage,
    resolvedPricingCountry,
    pricingRequestKey,
    pricingRetryTick,
  ]);
  const cardSourceContent = selectLandingPackageContent(
    planContent,
    pricingContent,
    currentFetchedPlanContent
  );
  const percentageSourceContent = selectLandingPercentageContent(
    planContent,
    pricingContent,
    currentFetchedPlanContent
  );
  const highestActivePackagePercentage = shouldRenderFlashdeals
    ? getHighestFlashdealCardPercentage(cardSourceContent, {
      // Trello 527 — match the tabs Plan.js renders: the 6-month card only
      // exists for international (non-id) visitors.
      includeSixMonth: (initialLanguage || "id") !== "id",
    })
    : (
      CARD_LABEL_PERCENTAGE_PAGE_SOURCES.includes(resolvedSource)
        ? getHighestCardLabelPercentage(percentageSourceContent)
        : getHighestActivePackagePercentage(percentageSourceContent, resolvedSource || page_source)
    );
  // [Trello 399 14AUG-3] On pages whose percentage comes from the package cards,
  // the real value is only knowable once those packages are in hand. Falling
  // back to the generic `highest_percentage` meanwhile painted 75% on first
  // render and swapped to 40% after the client fetch resolved. Blank the number
  // instead: the CTA renders without a percentage until the real one is known,
  // and never shows a wrong one.
  const resolvedLandingPageContent = highestActivePackagePercentage
    ? {
      ...landingPageContent,
      highest_percentage: highestActivePackagePercentage,
      discount_percentage: highestActivePackagePercentage,
    }
    : (needsPackageCards
      ? {
        ...landingPageContent,
        highest_percentage: null,
        discount_percentage: null,
      }
      : landingPageContent);
  const shouldHideLanguageButton = (
    CTA_PERCENTAGE_PAGE_SOURCES.includes(page_source)
    && typeof cfCountry === "string"
    && cfCountry.toUpperCase() !== "ID"
  ) || isOutsideIndonesiaCountry(cfCountry);
  useEffect(() => {
    if (!additionalProps || additionalProps.content_type !== "collab" || typeof window === "undefined") {
      return;
    }
    const {
      c,
      slug,
      pkg,
      landing_type,
    } = additionalProps;
    if (c) window.sessionStorage.setItem("collabPromoCode", c);
    if (slug) window.sessionStorage.setItem("collabSlug", slug);
    if (pkg) window.sessionStorage.setItem("collabPkg", pkg);
    if (landing_type) window.sessionStorage.setItem("collabLandingType", landing_type);
  }, [additionalProps]);

  function getUrlPageSources() {
    const resolved = resolveLandingCtaContext({
      pageSource: page_source,
      additionalProps: {
        ...(additionalProps || {}),
        cms_slug: (additionalProps && additionalProps.cms_slug) || cms_slug,
      },
      query: router.query,
      pathname: (typeof window !== "undefined" && window.location && window.location.pathname)
        || (router.asPath ? String(router.asPath).split("?")[0] : ""),
    });
    return getLandingQuestionnaireUrl(resolved.pageSource, resolved.additionalProps);
  }

  function renderHeader() {
    const heroImageDesktop = initialLanguage === "en" ? heroImageDesktopEN : heroImageDesktopID;
    const heroImageMobile = initialLanguage === "en" ? heroImageMobileEN : heroImageMobileID;
    const faqJsonLd = buildFaqJsonLd(initialLanguage);
    const heroPreloads = (
      <>
        <link rel="preload" as="image" href={heroImageMobile} media="(max-width: 960px)" fetchpriority="high" />
        <link rel="preload" as="image" href={heroImageDesktop} media="(min-width: 961px)" fetchpriority="high" />
        <link rel="preload" as="image" href={ratingBadgeSm} media="(max-width: 599px)" fetchpriority="high" />
        <link rel="preload" as="image" href={ratingBadgeLg} media="(min-width: 600px)" fetchpriority="high" />
      </>
    );
    const seoLang = (seo[initialLanguage] || seo.en || seo.id);
    if (!seoLang) {
      return (
        <Head>
          <title>Myfitsociety</title>
          {heroPreloads}
          {faqJsonLd && (
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJsonLd }} />
          )}
        </Head>
      );
    }
    const landingMeta = useHomepageSeo
      ? { title: seoLang.homepage, desc: seoLang.desc && seoLang.desc.homepage }
      : landingSeoFor(initialLanguage, page_source);
    return (
      <Head>
        <title>{landingMeta.title}</title>
        <meta name="description" content={landingMeta.desc} />

        <meta property="og:title" content={landingMeta.title} />
        <meta
          property="og:description"
          content={landingMeta.desc}
        />
        <meta property="og:image" content="https://myfitsociety.com/static/images/mfs/og-fb-001.jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="627" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://myfitsociety.com/static/images/mfs/og-fb-001.jpeg" />

        <link rel="icon" href="/static/favicons/favicon-32x32.png" type="image/png" />
        <link rel="apple-touch-icon" href="/static/favicons/apple-touch-icon.png" />

        <meta
          name="keywords"
          content="Myfitsociety, fitness app, diet app, body goals, workout program, diet program, fitness Indonesia, body goals 2025, health app, fitness, healthy diet, fitness training, workout, best fitness app, diet tips, fitness tips, personal trainer, body fitness, body goals app"
        />

        {heroPreloads}
        {faqJsonLd && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJsonLd }} />
        )}
      </Head>
    );
  }

  const redirectQuestionnaire = () => {
    followLandingCta(getUrlPageSources());
  };

  function renderPlan() {
    const packages = selectLandingPackageContent(
      planContent,
      pricingContent,
      currentFetchedPlanContent
    );
    if (packages) {
      return (
        <Plan
          planContent={packages}
          additionalProps={additionalProps}
          redirect={redirectQuestionnaire}
          isFlashdeals={shouldRenderFlashdeals}
        />
      );
    }
    return null;
  }

  return (
    <>
      {renderHeader()}
      <div className={classes.root}>
        <Navbar hideLanguageButton={shouldHideLanguageButton} />
        {/* <main> landmark — Lighthouse a11y: document needs a main landmark. */}
        <main>
          <HeroSection
            hideLanguageButton={shouldHideLanguageButton}
            landingPageContent={resolvedLandingPageContent}
            page_source={page_source}
            redirect={redirectQuestionnaire}
          />
          <Featured />
          <SuccessStory />
          <WhyMyfitsociety />
          <WorkoutPlan redirect={redirectQuestionnaire} />
          <YourProgram redirect={redirectQuestionnaire} />
          <Community />
          <BecomeMember redirect={redirectQuestionnaire} />
          <Comparison />
          <Testimony />
          {needsPackageCards ? (
            <section id="plan" style={{ scrollMarginTop: shouldRenderFlashdeals ? 76 : undefined }} />
          ) : null}
          {needsPackageCards ? renderPlan() : null}
          <FAQ />

          <Overview
            landingPageContent={resolvedLandingPageContent}
            page_source={page_source}
            redirect={redirectQuestionnaire}
          />
        </main>
        <Footer hideLanguageButton={shouldHideLanguageButton} />
      </div>
    </>
  );
};

export default LandingPageV2;
